import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  importProvidersFrom
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, TRANSLOCO_MISSING_HANDLER } from '@jsverse/transloco';
import { CustomTranslocoMissingHandler } from './core/transloco/CustomTranslocoMissingHandler';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { ConfigService } from './config/config.service';
import { OAuthModule, OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { environment } from '@htx/environment';
import { createAuthCodeFlowConfig } from './auth.config';
import { XauthInterceptor } from './core/interceptors/xauth.interceptor';
import { adessoApiConfigProvider } from './core/api/adesso-api.config';
import { provideContractAgreementApi } from './connector-ui/contracts/shared/contract-agreement-api.providers';
import { provideTransferProcessApi } from './connector-ui/contracts/shared/transfer-process-api.providers';
import { provideDashboardApi } from './connector-ui/dashboard/shared/dashboard-api.providers';
import { provideConnectorApi } from './connector-ui/connector/shared/connector-api.providers';
import { provideAssetApi } from './connector-ui/assets/shared/asset-api.providers';
import { provideCatalogApi } from './connector-ui/catalog/shared/catalog-api.providers';
import { providePolicyApi } from './connector-ui/policies/shared/policy-api.providers';
import { provideContractDefinitionApi } from './connector-ui/contracts/shared/contract-definition-api.providers';
import { provideDiscoveryApi } from './connector-ui/discovery/shared/discovery-api.providers';
import { provideDataPlaneApi } from './connector-ui/dataplanes/shared/data-plane-api.providers';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

function loadXAuthInterceptor() {
  if (environment.production) {
    return [XauthInterceptor];
  }
  return [];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([errorInterceptor, ...loadXAuthInterceptor()])),
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    provideNativeDateAdapter(),
    provideAppInitializer(async () => {
      const configService = inject(ConfigService);
      const oauthService = inject(OAuthService);

      await configService.loadConfig();

      if (!environment.oauth) {
        return;
      }

      oauthService.configure(createAuthCodeFlowConfig({ oauth: environment.oauth }));
      oauthService.setStorage(localStorage);

      try {
        await oauthService.loadDiscoveryDocumentAndTryLogin();
      } catch (err) {
        console.error('OIDC initialization failed:', err);
        return;
      }

      if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      if (oauthService.hasValidAccessToken()) {
        return;
      }

      oauthService.initLoginFlow();
      return new Promise<void>(() => {
        /* never resolves — browser is navigating away to Keycloak */
      });
    }),
    provideOAuthClient(),
    importProvidersFrom(
      OAuthModule.forRoot({
        resourceServer: {
          allowedUrls: [environment.apiUrl, environment.managementUrl],
          sendAccessToken: true
        }
      })
    ),
    provideTransloco({
      config: {
        availableLangs: ['de', 'en'],
        defaultLang: 'de',
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    { provide: TRANSLOCO_MISSING_HANDLER, useClass: CustomTranslocoMissingHandler },
    adessoApiConfigProvider,
    provideContractAgreementApi(),
    provideTransferProcessApi(),
    provideDashboardApi(),
    provideConnectorApi(),
    provideAssetApi(),
    provideCatalogApi(),
    providePolicyApi(),
    provideContractDefinitionApi(),
    provideDiscoveryApi(),
    provideDataPlaneApi()
  ]
};
